import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const thisMonth = () => new Date().toISOString().slice(0, 7);

function useReport(endpoint) {
    const [period, setPeriod] = useState(thisMonth());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(endpoint, { params: { period } });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No payroll for period");
            setData(null);
        } finally { setLoading(false); }
    };
    const download = async (ext) => {
        try {
            const res = await api.get(`${endpoint}.${ext}`, { params: { period }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = `${endpoint.split("/").pop()}-${period}.${ext}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`${ext.toUpperCase()} downloaded`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };
    return { period, setPeriod, data, loading, load, download };
}

function Toolbar({ r, prefix }) {
    return (
        <div className="bg-white border border-ink/10 p-5 flex items-end gap-4 flex-wrap">
            <div>
                <label className="overline block mb-2">Pay period</label>
                <input type="month" value={r.period} onChange={(e) => r.setPeriod(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid={`${prefix}-period-input`} />
            </div>
            <button onClick={r.load} disabled={r.loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid={`${prefix}-load-button`}>
                {r.loading ? "Loading…" : "Generate"}
            </button>
            {r.data && (
                <div className="ml-auto flex gap-2">
                    <button onClick={() => r.download("pdf")} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid={`${prefix}-pdf-button`}><Download className="w-4 h-4" /> PDF</button>
                    <button onClick={() => r.download("xlsx")} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid={`${prefix}-xlsx-button`}><Download className="w-4 h-4" /> Excel</button>
                </div>
            )}
        </div>
    );
}

function Summary({ label, value, sub, testid }) {
    return (
        <div className="bg-klein text-white p-6" data-testid={testid}>
            <div className="overline !text-white/70">{label}</div>
            <div className="mt-2 font-display font-black text-3xl tabular-nums">{value}</div>
            <div className="mt-1 text-xs text-white/70">{sub}</div>
        </div>
    );
}

const Th = ({ children, right }) => <th className={`overline px-4 py-2 ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Empty = ({ text }) => <div className="bg-white border border-ink/10 p-8 text-sm text-ink/50">{text}</div>;

export function NhfReport() {
    const r = useReport("/reports/nhf-remittance");
    const d = r.data;
    return (
        <div className="space-y-6">
            <Toolbar r={r} prefix="nhf" />
            {d && (
                <div className="space-y-6" data-testid="nhf-report-result">
                    <Summary label="Total NHF remittance · Federal Mortgage Bank" value={fmt(d.grand_total)} sub={`${d.employer} · ${d.period} · ${d.headcount} employees`} testid="nhf-total" />
                    {d.rows.length === 0 ? <Empty text="No NHF deductions in this payroll. Tick “Deduct NHF monthly” on staff profiles, then recalculate the payroll." /> : (
                        <div className="bg-white border border-ink/10 overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead><tr className="border-b border-ink/10 bg-bone/40"><Th>S/N</Th><Th>Staff ID</Th><Th>Name</Th><Th>NHF number</Th><Th right>Basic</Th><Th right>NHF 2.5%</Th></tr></thead>
                                <tbody>
                                    {d.rows.map((x, i) => (
                                        <tr key={`${x.employee_no}-${i}`} className="border-b border-ink/5" data-testid={`nhf-row-${x.employee_no || i}`}>
                                            <td className="px-4 py-2 text-ink/50">{i + 1}</td>
                                            <td className="px-4 py-2 text-ink/70">{x.employee_no || "—"}</td>
                                            <td className="px-4 py-2 text-ink">{x.employee_name}</td>
                                            <td className="px-4 py-2 text-ink/70">{x.nhf_number || <span className="text-amber-700">Missing</span>}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{fmt(x.basic_salary)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums font-medium">{fmt(x.amount)}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-bone/40 font-semibold"><td colSpan={4} className="px-4 py-2">Grand total</td><td className="px-4 py-2 text-right tabular-nums">{fmt(d.grand_basic)}</td><td className="px-4 py-2 text-right tabular-nums">{fmt(d.grand_total)}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ProviderTable({ g }) {
    return (
        <div className="bg-white border border-ink/10" data-testid={`hmo-provider-${g.provider.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="px-6 py-3 bg-bone/30 flex items-center justify-between">
                <div className="font-medium text-ink">{g.provider} <span className="text-ink/50 text-xs font-normal ml-2">({g.headcount} staff)</span></div>
                <div className="text-sm tabular-nums">Subtotal <span className="font-semibold text-klein">{fmt(g.subtotal)}</span></div>
            </div>
            <table className="w-full text-sm border-collapse">
                <thead><tr className="border-b border-ink/10"><Th>S/N</Th><Th>Staff ID</Th><Th>Name</Th><Th>Department</Th><Th right>HMO deduction</Th></tr></thead>
                <tbody>
                    {g.rows.map((x, i) => (
                        <tr key={`${x.employee_no}-${i}`} className="border-b border-ink/5">
                            <td className="px-4 py-2 text-ink/50">{i + 1}</td>
                            <td className="px-4 py-2 text-ink/70">{x.employee_no || "—"}</td>
                            <td className="px-4 py-2 text-ink">{x.employee_name}</td>
                            <td className="px-4 py-2 text-ink/70">{x.department || "—"}</td>
                            <td className="px-4 py-2 text-right tabular-nums font-medium">{fmt(x.amount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function HmoReport() {
    const r = useReport("/reports/hmo-deductions");
    const d = r.data;
    return (
        <div className="space-y-6">
            <Toolbar r={r} prefix="hmo" />
            {d && (
                <div className="space-y-6" data-testid="hmo-report-result">
                    <Summary label="Total HMO deductions" value={fmt(d.grand_total)} sub={`${d.employer} · ${d.period} · ${d.providers.length} provider(s) · ${d.headcount} staff`} testid="hmo-total" />
                    {d.providers.length === 0 ? <Empty text="No HMO deductions in this payroll. Set an HMO amount and provider on staff profiles, then recalculate the payroll." /> : d.providers.map((g) => <ProviderTable key={g.provider} g={g} />)}
                </div>
            )}
        </div>
    );
}
